import {
  DefaultMantineColor,
  useMantineColorScheme,
  List,
  Group,
  Tooltip,
  Text,
} from "@mantine/core"
import dayjs from "dayjs"
import { match, P } from "ts-pattern"
import { DateOnly, TimeOnly } from "../../../@client/System"
import { IconType, RenderIcon } from "./icon"
import { useTranslations } from "../../../utils/use-t"
import {
  ActivityInPeriod,
  ActivityType,
  OverlapStatus_Case,
} from "../../../@client/Theasoft_Absences"
import { ActivityTypeCategory } from "../../../@client/Theasoft_DataClasses_Dispo"

interface Props {
  activity: ActivityInPeriod
  name: string
  subtitle: string
  date: DateOnly
  time: TimeOnly | null
  icon?: IconType
  color: DefaultMantineColor
  status: OverlapStatus_Case
  highlightTime?: boolean
}

function isImportantCategory(categoryId: ActivityTypeCategory) {
  return (
    categoryId === "Performance" ||
    categoryId === "Premiere" ||
    categoryId === "RevivalPerformance"
  ) //categoryId >= 2000
}

function translateActivityType(v: ActivityType) {
  return (
    match(v)
      .with({ Case: "PossibleCast" }, () => "mgl. Besetzung")
      .with({ Case: "Cast" }, () => "")
      // TODO: implement properly
      .with({ Case: "Premiere" }, () => "")
      .otherwise(() => null)
  )
}

function Item({
  activity,
  name,
  subtitle,
  date,
  time,
  color,
  icon,
  status,
  highlightTime: highlightTimeProps,
}: Props) {
  const ts = useTranslations()
  const i: IconType | undefined =
    status === "Overlapping"
      ? "IconAlertTriangle"
      : status === "SameAsReturnDay"
        ? "IconAlertCircle"
        : icon
  const statusTooltip = match(status)
    .with(
      "NotOverlapping",
      () => ts.absences.t("AppointmentIsNotInPeriod") // "Termin ist außerhalb des beantragten Zeitraums"
    )
    .with("Overlapping", () => ts.absences.t("AppointmentIsInPeriod"))
    .with("SameAsReturnDay", () => ts.absences.t("AppointmentIsOnDayOfReturn"))
    .exhaustive()
  const highlightTime = match(status)
    .with("SameAsReturnDay", () => true)
    .otherwise(() => highlightTimeProps)
  const categoryId = activity?.activity?.activityType?.category

  const importantCategory =
    categoryId === undefined ? false : isImportantCategory(categoryId)
  const title = activity.activity?.activityType.name || name
  const { colorScheme } = useMantineColorScheme()
  return (
    <List.Item
      icon={
        <Group>
          <Tooltip label={statusTooltip}>
            <div>
              <RenderIcon color={color} icon={i ? i : "IconCircle"} />
            </div>
          </Tooltip>
          {activity.activity?.isSecret ? (
            <Tooltip label="Geheim">
              <div>
                <RenderIcon
                  color={colorScheme === "dark" ? "dark.1" : "dark"}
                  icon={"IconEyeOff"}
                />
              </div>
            </Tooltip>
          ) : null}
        </Group>
      }
    >
      <Text c="bright" span fw={importantCategory ? 700 : 500}>
        {title}{" "}
        {importantCategory
          ? activity.activity?.production?.title.toUpperCase()
          : activity.activity?.production?.title}
      </Text>{" "}
      <Text c="bright" span fw={500}>
        {subtitle}
        {translateActivityType(activity.activityType)
          ? " (" + translateActivityType(activity.activityType) + ")"
          : null}
      </Text>{" "}
      <Text
        span
        fw={highlightTime ? 400 : undefined}
        c={highlightTime ? "bright" : "dimmed"}
      >
        {dayjs(date).format("ddd L")}
        {time ? " " + dayjs("0000-00-00 " + time).format("LT") : null}
        {match(activity.differentStartTime)
          .with(P.nullish, () => null)
          .otherwise(
            (cast) =>
              ` (${match(cast.reason)
                .with("ArrivalTime", () => ts.appServer.t("ArrivalTime"))
                .with("DepartureTime", () => ts.appServer.t("DepartureTime"))
                .with("MaskTime", () => ts.appServer.t("MaskTime"))
                .with("PresenceStartTime", () =>
                  ts.absences.t("PresenceStartTime")
                )
                .otherwise((other) => other)}` +
              " " +
              dayjs(cast.time).format("LT") +
              ")"
          )}
      </Text>{" "}
      {/* (activity.activity?.location?.name ) ? (  //Bühnen reine, wenm bei allen Typen verfügbar, dann nur wenn ausserhaltb
            <Text span c="dimmed">
              {activity.activity?.location?.name}
            </Text>
          ) : null*/}
    </List.Item>
  )
}

export function ActivityItem({
  activity,
  highlightTime,
}: {
  activity: ActivityInPeriod
  highlightTime?: boolean
}) {
  const ts = useTranslations()
  const color = (v: OverlapStatus_Case) =>
    match<OverlapStatus_Case, DefaultMantineColor>(v)
      .with("NotOverlapping", () => "bright")
      .with("Overlapping", () => "red.6")
      .with("SameAsReturnDay", () => "orange.4")
      .exhaustive()

  const translate = (v: string) => {
    // TODO
    return match<string, string>(v)
      .with("MiscRehearsal", () => "Probe (misc)")
      .with("Performance", () => "Vorführung")
      .with("Rehearsal", () => "Probe")
      .with("Premiere", () => "Premiere")
      .otherwise((v) => v)
  }

  const [icon, c] = match<
    ActivityType,
    [IconType | undefined, DefaultMantineColor]
  >(activity.activityType)
    .with({ Case: "StartOfAbsence" }, () => ["IconDoorExit", "blue"])
    .with({ Case: "EndOfAbsence" }, () => ["IconDoorEnter", "blue"])
    .with({ Case: "PossibleCast" }, () => [
      undefined,
      color(activity.overlapStatus.Case),
    ])
    .with({ Case: "Cast" }, () => [
      undefined,
      color(activity.overlapStatus.Case),
    ])
    .with({ Case: "Premiere" }, () => [
      undefined,
      color(activity.overlapStatus.Case),
    ])
    .with({ Case: "Other" }, () => [
      undefined,
      color(activity.overlapStatus.Case),
    ])
    .with({ Case: "Secret" }, () => [
      undefined,
      color(activity.overlapStatus.Case),
    ])
    .otherwise(() => [undefined, color(activity.overlapStatus.Case)])
  const name = match(activity.activityType)
    .with({ Case: "StartOfAbsence" }, () => ts.absences.t("StartOfAbsence"))
    .with({ Case: "EndOfAbsence" }, () => ts.absences.t("EndOfAbsence"))
    .otherwise(() => translate(activity.activityTypeName) + " " + activity.name)
  return (
    <Item
      activity={activity}
      name={name}
      subtitle={activity.role.map((v) => v.name).join(", ")}
      date={activity.date}
      time={activity.time}
      highlightTime={highlightTime}
      color={c}
      icon={icon}
      status={activity.overlapStatus.Case}
    />
  )
}
